import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  listStorefrontPublicCategoryPickerOptions,
  listStorefrontPublicCollectionPickerOptions,
  listStorefrontPublicMediaPickerOptions,
  listStorefrontPublicProductPickerOptions,
  validateStorefrontPublicContentRefs,
  type ValidateStorefrontPublicContentRefsInput,
} from '../services/storefront-public-content-picker.service';
import { storefrontPublicContentResolverKeys } from './use-storefront-public-content-resolver';
import type { SupabaseClient } from '../supabase/client';
import type {
  PublicMediaRole,
  StorefrontPersistenceValidationSummary,
  StorefrontPublicCategoryPickerOption,
  StorefrontPublicCollectionPickerOption,
  StorefrontPublicContentPickerResult,
  StorefrontPublicMediaPickerOption,
  StorefrontPublicProductPickerOption,
} from '../types/storefront-engine';

export {
  createStorefrontPublicContentRefsFingerprint,
  storefrontPublicContentResolverKeys,
  useStorefrontResolvedPublicContentRefs,
} from './use-storefront-public-content-resolver';

export const storefrontPublicContentPickerKeys = {
  all: ['storefront-public-content-picker'] as const,
  products: (storeId: string, search: string, categoryId: string | null) =>
    ['storefront-public-content-picker', 'admin', 'products', storeId, search, categoryId] as const,
  categories: (storeId: string, search: string) =>
    ['storefront-public-content-picker', 'admin', 'categories', storeId, search] as const,
  media: (storeId: string, search: string, role: PublicMediaRole) =>
    ['storefront-public-content-picker', 'admin', 'media', storeId, search, role] as const,
  promotionalAds: (storeId: string, search: string) =>
    ['storefront-public-content-picker', 'admin', 'promotional-ads', storeId, search] as const,
  resolvedRefs: storefrontPublicContentResolverKeys.resolvedRefs,
};

export function useStorefrontPublicProductPickerOptions(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly search: string;
    readonly categoryId?: string | null;
    readonly supabaseUrl: string;
  },
): UseQueryResult<StorefrontPublicProductPickerOption[]> {
  const storeId = args.storeId?.trim() ?? '';
  const search = args.search.trim();
  const categoryId = args.categoryId ?? null;
  return useQuery({
    queryKey: storeId
      ? storefrontPublicContentPickerKeys.products(storeId, search, categoryId)
      : storefrontPublicContentPickerKeys.products('pending', search, categoryId),
    enabled: storeId !== '',
    queryFn: () =>
      listStorefrontPublicProductPickerOptions(
        client,
        { storeId, search, categoryId },
        { supabaseUrl: args.supabaseUrl },
      ),
    staleTime: 30_000,
  });
}

export function useStorefrontPublicCategoryPickerOptions(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly search: string;
    readonly supabaseUrl: string;
  },
): UseQueryResult<StorefrontPublicCategoryPickerOption[]> {
  const storeId = args.storeId?.trim() ?? '';
  const search = args.search.trim();
  return useQuery({
    queryKey: storeId
      ? storefrontPublicContentPickerKeys.categories(storeId, search)
      : storefrontPublicContentPickerKeys.categories('pending', search),
    enabled: storeId !== '',
    queryFn: () =>
      listStorefrontPublicCategoryPickerOptions(
        client,
        { storeId, search },
        { supabaseUrl: args.supabaseUrl },
      ),
    staleTime: 30_000,
  });
}

export function useStorefrontPublicMediaPickerOptions(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly search: string;
    readonly role?: PublicMediaRole;
    readonly supabaseUrl: string;
  },
): UseQueryResult<StorefrontPublicMediaPickerOption[]> {
  const storeId = args.storeId?.trim() ?? '';
  const search = args.search.trim();
  const role = args.role ?? 'section-image';
  return useQuery({
    queryKey: storeId
      ? storefrontPublicContentPickerKeys.media(storeId, search, role)
      : storefrontPublicContentPickerKeys.media('pending', search, role),
    enabled: storeId !== '',
    queryFn: () =>
      listStorefrontPublicMediaPickerOptions(
        client,
        { storeId, search, role },
        { supabaseUrl: args.supabaseUrl },
      ),
    staleTime: 30_000,
  });
}

export function useStorefrontPublicCollectionPickerOptions(): readonly StorefrontPublicCollectionPickerOption[] {
  return listStorefrontPublicCollectionPickerOptions();
}

export function useValidateStorefrontPublicContentRefs(
  client: SupabaseClient,
): UseMutationResult<
  StorefrontPublicContentPickerResult<StorefrontPersistenceValidationSummary>,
  unknown,
  ValidateStorefrontPublicContentRefsInput
> {
  return useMutation({
    mutationFn: (input) => validateStorefrontPublicContentRefs(client, input),
  });
}
